import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import { useQueryClient } from "react-query";
import axios from "../config/axiosInstance";
import { BANNERS_ADMIN } from "../config/apiConfig";
import {
  getBannerStateKey,
  STATUS_CONFIG,
  TARGET_LOOKUP,
  formatDateTime,
} from "../components/banners/BannerCard";
import { FaRegEdit, FaTrash } from "react-icons/fa";

const BannerManagerPage = () => {
  const {
    data: banners = [],
    isLoading,
    error,
  } = useQuery(
    ["adminBanners"],
    async () => {
      const { data } = await axios.get(`${BANNERS_ADMIN.LIST}`);
      return data.data;
    },
    { staleTime: 1000 * 60 }
  );

  const queryClient = useQueryClient();
  const [deletingBannerId, setDeletingBannerId] = useState(null);

  const stats = useMemo(() => {
    const now = Date.now();
    const summary = {
      total: banners.length,
      active: 0,
      upcoming: 0,
      expired: 0,
    };
    banners.forEach((banner) => {
      const key = getBannerStateKey(banner.startDate, banner.endDate, now);
      if (key === "active") summary.active += 1;
      else if (key === "upcoming") summary.upcoming += 1;
      else if (key === "expired") summary.expired += 1;
    });
    return summary;
  }, [banners]);

  const sortedBanners = useMemo(() => {
    return [...banners].sort((a, b) => {
      const startA = new Date(a.startDate).getTime() || 0;
      const startB = new Date(b.startDate).getTime() || 0;
      return startB - startA;
    });
  }, [banners]);

  const statCards = [
    {
      label: "Ativos agora",
      value: stats.active,
      description: "Exibidos no momento.",
      containerClass: "border-emerald-200 bg-emerald-50/70",
      labelClass: "text-emerald-600",
      valueClass: "text-emerald-900",
    },
    {
      label: "Programados",
      value: stats.upcoming,
      description: "Futuros aguardando liberação.",
      containerClass: "border-sky-200 bg-sky-50/70",
      labelClass: "text-sky-600",
      valueClass: "text-sky-900",
    },
    {
      label: "Expirados",
      value: stats.expired,
      description: "Histórico de campanhas.",
      containerClass: "border-amber-200 bg-amber-50/70",
      labelClass: "text-amber-600",
      valueClass: "text-amber-900",
    },
    {
      label: "Total",
      value: stats.total,
      description: "Estratégias registradas.",
      containerClass: "border-slate-200 bg-slate-50/70",
      labelClass: "text-slate-600",
      valueClass: "text-slate-900",
    },
  ];

  const lastRefresh = useMemo(() => {
    return new Date().toLocaleString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [banners]);

  const handleDelete = async (bannerId) => {
    const confirm = window.confirm(
      "Tem certeza que deseja remover este banner? A ação não pode ser desfeita."
    );
    if (!confirm) return;
    setDeletingBannerId(bannerId);
    try {
      await axios.delete(BANNERS_ADMIN.DELETE(bannerId));
      queryClient.invalidateQueries(["adminBanners"]);
    } catch (error) {
      console.error(error);
      alert(
        error.response?.data?.message ||
          "Não foi possível excluir o banner. Tente novamente."
      );
    } finally {
      setDeletingBannerId(null);
    }
  };

  return (
    <section className="space-y-6">
      <header className="space-y-4 rounded-2xl border border-slate-200 bg-white/90 p-6 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center md:justify-between">
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-slate-900">
              Painel de banners
            </h1>
          </div>
          <Link
            to="/admin/banners/create"
            className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
          >
            Novo banner
          </Link>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {statCards.map((stat) => (
            <article
              key={stat.label}
              className={`flex flex-col justify-between gap-1 rounded-2xl border p-3 shadow-sm ${stat.containerClass}`}
            >
              <div
                className={`flex items-center justify-between text-xs font-semibold uppercase tracking-[0.3em] ${stat.labelClass}`}
              >
                <span>{stat.label}</span>
                <span aria-hidden="true">•</span>
              </div>
              <p className={`text-2xl font-semibold ${stat.valueClass}`}>
                {stat.value}
              </p>
              <p className="text-xs text-slate-500">{stat.description}</p>
            </article>
          ))}
        </div>
      </header>

      <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-end gap-3">
          <span className="text-sm text-slate-500">
            {isLoading ? "Atualizando…" : `${sortedBanners.length} itens`}
          </span>
        </div>
        {isLoading ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-12 text-center text-sm text-slate-500">
            Carregando banners…
          </div>
        ) : sortedBanners.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-12 text-center text-sm text-slate-500">
            Nenhum banner registrado ainda. Use “Novo banner” para criar o
            primeiro.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-3xl border border-slate-200 bg-slate-50/70 shadow-inner">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-white/80 text-xs uppercase tracking-[0.3em] text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Banner</th>
                  <th className="px-4 py-3 text-left">Visibilidade</th>
                  <th className="px-4 py-3 text-left">Período</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-center">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white/90">
                {sortedBanners.map((banner) => {
                  const key = getBannerStateKey(
                    banner.startDate,
                    banner.endDate
                  );
                  const status = STATUS_CONFIG[key] || STATUS_CONFIG.draft;
                  const target = TARGET_LOOKUP[banner.targetAudience];
                  return (
                    <tr key={banner.id} className="group">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className="h-12 w-20 rounded-2xl bg-slate-900 bg-cover bg-center"
                            style={{
                              backgroundImage: banner.imageSignedUrl
                                ? `url(${banner.imageSignedUrl})`
                                : undefined,
                            }}
                          />
                          <div>
                            <p className="text-sm font-semibold text-slate-900">
                              {banner.title || "Sem título"}
                            </p>
                            <p className="text-2xs text-slate-500 uppercase tracking-[0.3em]">
                              {banner.showTitle === false
                                ? "Título oculto"
                                : "Título ativo"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm font-semibold text-slate-900">
                          {target?.label || "Público geral"}
                        </span>
                        <p className="text-xs text-slate-500">
                          {target?.description}
                        </p>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-slate-600">
                          {formatDateTime(banner.startDate)} -{" "}
                          {formatDateTime(banner.endDate)}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.3em] ${status.badge}`}
                        >
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-center gap-3">
                          <Link
                            to={`/admin/banners/${banner.id}/edit`}
                            className="text-xs font-semibold uppercase tracking-[0.3em] text-primary hover:text-primary/80"
                          >
                            <span className="flex items-center gap-1">
                              <FaRegEdit fontSize={14} />
                            </span>
                          </Link>
                          <button
                            onClick={() => handleDelete(banner.id)}
                            disabled={deletingBannerId === banner.id}
                            className="text-xs font-semibold uppercase tracking-[0.3em] text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-40 flex items-center gap-1"
                          >
                            <FaTrash fontSize={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {error && (
          <p className="text-sm text-red-600">
            Não foi possível carregar os dados. Atualize a página.
          </p>
        )}
      </section>
    </section>
  );
};

export default BannerManagerPage;
